import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  CompleteConnectorOAuthRequest,
  ConnectConnectorRequest,
  ConnectConnectorResponse,
  ConnectConnectorResult,
  Connector,
  ConnectorDiscovery,
  ConnectorDiscoveryResponse,
  ConnectorPayload,
  ConnectorResponse,
  ConnectorsResponse,
  ConnectorTool,
  ConnectorToolsResponse,
  OrgConnector,
  OrgConnectorResponse,
  OrgConnectorsResponse,
} from "./types";

// Export types
export * from "./types";

const ENDPOINTS = {
  CONNECTORS: {
    LIST: "/api/v2/org/:organization_id/user/:user_id/connectors",
    CREATE: "/api/v2/org/:organization_id/user/:user_id/connectors",
    DISCOVER: "/api/v2/org/:organization_id/user/:user_id/connector-discovery",
    UPDATE:
      "/api/v2/org/:organization_id/user/:user_id/connectors/:connector_id",
    DELETE:
      "/api/v2/org/:organization_id/user/:user_id/connectors/:connector_id",
    TOOLS:
      "/api/v2/org/:organization_id/user/:user_id/connectors/:connector_id/tools",
    CONNECT:
      "/api/v2/org/:organization_id/user/:user_id/connectors/:connector_id/connect",
    COMPLETE_OAUTH:
      "/api/v2/org/:organization_id/user/:user_id/connectors/:connector_id/connect/complete",
    DISCONNECT:
      "/api/v2/org/:organization_id/user/:user_id/connector-connections/:connector_connection_id",
  },
  ORG: {
    LIST: "/api/v2/org/:organization_id/connectors",
    CREATE: "/api/v2/org/:organization_id/connectors",
    DISCOVER: "/api/v2/org/:organization_id/connector-discovery",
    UPDATE: "/api/v2/org/:organization_id/connectors/:connector_id",
    DELETE: "/api/v2/org/:organization_id/connectors/:connector_id",
  },
};

export class ConnectorsAPI {
  private client: CohesiveAPIClient;

  constructor(client: CohesiveAPIClient) {
    this.client = client;
  }

  /**
   * Get the connectors available to the current user, each with their connection if they have one.
   */
  async list(config?: AxiosRequestConfig): Promise<Connector[]> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<ConnectorsResponse>(
      route(ENDPOINTS.CONNECTORS.LIST, {
        organization_id: orgId || "",
        user_id: userId || "",
      }),
      config,
    );

    return response.data.data.connectors;
  }

  /**
   * Create a connector of the current user's own. It may reuse a global or organization slug,
   * overriding that connector for them.
   */
  async create(
    data: ConnectorPayload,
    config?: AxiosRequestConfig,
  ): Promise<Connector> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<ConnectorResponse>(
      route(ENDPOINTS.CONNECTORS.CREATE, {
        organization_id: orgId || "",
        user_id: userId || "",
      }),
      data,
      config,
    );

    return response.data.data;
  }

  /**
   * Probe an MCP server for what a connector of the current user's own to it needs. An unreachable
   * server is refused with code `connector_unreachable`.
   */
  async discover(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ConnectorDiscovery> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<ConnectorDiscoveryResponse>(
      route(ENDPOINTS.CONNECTORS.DISCOVER, {
        organization_id: orgId || "",
        user_id: userId || "",
      }),
      { url },
      config,
    );

    return response.data.data;
  }

  /**
   * Replace the definition of one of the current user's own connectors. Refused with a 409 when it
   * changes the URL, inputs or auth while they are connected.
   */
  async update(
    connectorId: string,
    data: ConnectorPayload,
    config?: AxiosRequestConfig,
  ): Promise<Connector> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.put<ConnectorResponse>(
      route(ENDPOINTS.CONNECTORS.UPDATE, {
        organization_id: orgId || "",
        user_id: userId || "",
        connector_id: connectorId,
      }),
      data,
      config,
    );

    return response.data.data;
  }

  /**
   * Delete one of the current user's own connectors and their connection to it.
   */
  async delete(
    connectorId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.CONNECTORS.DELETE, {
        organization_id: orgId || "",
        user_id: userId || "",
        connector_id: connectorId,
      }),
      config,
    );
  }

  /**
   * List the tools a connector's MCP server exposes through the current user's connection, live.
   * Refused with code `connector_not_connected`, `connector_auth_rejected` or
   * `connector_unreachable`.
   */
  async tools(
    connectorId: string,
    config?: AxiosRequestConfig,
  ): Promise<ConnectorTool[]> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<ConnectorToolsResponse>(
      route(ENDPOINTS.CONNECTORS.TOOLS, {
        organization_id: orgId || "",
        user_id: userId || "",
        connector_id: connectorId,
      }),
      config,
    );

    return response.data.data.tools;
  }

  /**
   * Connect a connector, replacing the user's previous connection to it.
   */
  async connect(
    connectorId: string,
    data: ConnectConnectorRequest,
    config?: AxiosRequestConfig,
  ): Promise<ConnectConnectorResult> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<ConnectConnectorResponse>(
      route(ENDPOINTS.CONNECTORS.CONNECT, {
        organization_id: orgId || "",
        user_id: userId || "",
        connector_id: connectorId,
      }),
      data,
      config,
    );

    return response.data.data;
  }

  /**
   * Finish an OAuth connect with the code and state its callback handed back.
   */
  async completeOAuth(
    connectorId: string,
    data: CompleteConnectorOAuthRequest,
    config?: AxiosRequestConfig,
  ): Promise<ConnectConnectorResult> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<ConnectConnectorResponse>(
      route(ENDPOINTS.CONNECTORS.COMPLETE_OAUTH, {
        organization_id: orgId || "",
        user_id: userId || "",
        connector_id: connectorId,
      }),
      data,
      config,
    );

    return response.data.data;
  }

  /**
   * Revoke one of the current user's connections.
   */
  async disconnect(
    connectorConnectionId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.CONNECTORS.DISCONNECT, {
        organization_id: orgId || "",
        user_id: userId || "",
        connector_connection_id: connectorConnectionId,
      }),
      config,
    );
  }

  /**
   * Get the organization's own connectors, as its admins configure them. Global connectors are not
   * included.
   */
  async orgList(config?: AxiosRequestConfig): Promise<OrgConnector[]> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.get<OrgConnectorsResponse>(
      route(ENDPOINTS.ORG.LIST, { organization_id: orgId || "" }),
      config,
    );

    return response.data.data.connectors;
  }

  /**
   * Create a connector every member of the organization can connect to.
   */
  async orgCreate(
    data: ConnectorPayload,
    config?: AxiosRequestConfig,
  ): Promise<OrgConnector> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.post<OrgConnectorResponse>(
      route(ENDPOINTS.ORG.CREATE, { organization_id: orgId || "" }),
      data,
      config,
    );

    return response.data.data;
  }

  /**
   * Probe an MCP server for what an organization connector to it needs. An unreachable server is
   * refused with code `connector_unreachable`.
   */
  async orgDiscover(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ConnectorDiscovery> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.post<ConnectorDiscoveryResponse>(
      route(ENDPOINTS.ORG.DISCOVER, { organization_id: orgId || "" }),
      { url },
      config,
    );

    return response.data.data;
  }

  /**
   * Replace an organization connector's definition. Refused with a 409 when it changes the URL,
   * inputs or auth while members are connected.
   */
  async orgUpdate(
    connectorId: string,
    data: ConnectorPayload,
    config?: AxiosRequestConfig,
  ): Promise<OrgConnector> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.put<OrgConnectorResponse>(
      route(ENDPOINTS.ORG.UPDATE, {
        organization_id: orgId || "",
        connector_id: connectorId,
      }),
      data,
      config,
    );

    return response.data.data;
  }

  /**
   * Delete an organization connector and every member's connection to it.
   */
  async orgDelete(
    connectorId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();

    await this.client.axios.delete(
      route(ENDPOINTS.ORG.DELETE, {
        organization_id: orgId || "",
        connector_id: connectorId,
      }),
      config,
    );
  }
}
